// src/pages/AIPage.js – نسخهٔ نهایی با عرض هماهنگ با باقی صفحات در دسکتاپ

import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useAuthContext } from '../context/AuthContext';
import { BsSend, BsCopy, BsSearch, BsLightbulb, BsX } from 'react-icons/bs';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import ContentCard from '../components/Product/ContentCard';
import BookCover from '../components/library/BookCover';

const BG_IMAGE = 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/AI.png';

// ─── ارتفاع نوار بالا و پایین AppLayout (باید با مقادیر واقعی تطبیق داده شود) ───
const TOPBAR_HEIGHT = 56;    // px — ارتفاع AppTopBar
const BOTTOMNAV_HEIGHT = 64; // px — ارتفاع BottomNavigation

const initialMessage = `سلام. من دستیار هوشمند «فرزانه» هستم، طراحی شده‌ام تا شما را در درک عمیق‌تر دیدگاه‌ها و آثار استاد مصطفی ملکیان همراهی کنم. من با دسترسی به سخنرانی‌ها، کتاب‌ها و مقالات مرتبط، جامع‌ترین پاسخ‌ها را ارائه می‌دهم.

شما می‌توانید در مورد این موارد از من بپرسید:

• مفاهیم اصلی در فلسفه ملکیان (مانند خردورزی، اخلاق، معنویت).
• مطالب ارائه شده در درس‌گفتارها و دوره‌های سایت.
• توصیه کتاب‌ها و مقالات مرتبط با موضوع مورد نظر شما.

برای مثال:

• «در کدام سخنرانی‌ها به آثار میشل فوکو اشاره شده است؟»
• «کدام آثار استاد در ارتباط با روانشناسی مثبت‌نگر است؟»
• «رابطه عقلانیت و معنویت از دیدگاه ایشان چیست؟»

لطفاً پرسش خود را مطرح کنید.`;

// ─────────────────────────────────────────────────────────────────────────────
// پارسر محتوای پیام
// سه نوع بلوک تولید می‌کند:
//   { type: 'text',  value: string }
//   { type: 'cards', items: [{title, url, itemType}] }
//   { type: 'link',  title: string, url: string }   ← لینک صفحات عادی
// ─────────────────────────────────────────────────────────────────────────────
const CARD_LINK_REGEX = /(?:🎓|📚|🎙️|-)?\s*\[([^\]]+)\]\((\/(?:products|books|media)\/[^)]+)\)/g;
const PAGE_LINK_REGEX = /\[([^\]]+)\]\((\/[^)]+)\)/g;

// تمیز کردن تگ‌های HTML از متن
function stripHtml(str) {
  return str.replace(/<[^>]*>/g, '');
}

function parseMessageContent(rawContent) {
  const content = stripHtml(rawContent);
  const blocks = [];
  const lines = content.split('\n');
  let textBuffer = [];
  let currentCards = [];

  const flushText = () => {
    const txt = textBuffer.join('\n').trim();
    if (txt) blocks.push({ type: 'text', value: txt });
    textBuffer = [];
  };

  const flushCards = () => {
    if (currentCards.length) {
      blocks.push({ type: 'cards', items: currentCards });
      currentCards = [];
    }
  };

  for (const line of lines) {
    // ابتدا بررسی لینک محصول/کتاب
    CARD_LINK_REGEX.lastIndex = 0;
    const cardMatch = CARD_LINK_REGEX.exec(line);
    if (cardMatch) {
      const title = cardMatch[1].trim();
      const url = cardMatch[2].trim();
      const itemType = url.startsWith('/books/') ? 'book' : 'product';
      flushText();
      currentCards.push({ title, url, itemType });
      continue;
    }

    flushCards();

    // بررسی لینک صفحات عادی در خط
    PAGE_LINK_REGEX.lastIndex = 0;
    const pageMatch = PAGE_LINK_REGEX.exec(line);
    if (pageMatch) {
      // متن قبل از لینک
      const before = line.slice(0, pageMatch.index).trim();
      if (before) textBuffer.push(before);
      flushText();
      blocks.push({ type: 'link', title: pageMatch[1].trim(), url: pageMatch[2].trim() });
      const after = line.slice(pageMatch.index + pageMatch[0].length).trim();
      if (after) textBuffer.push(after);
    } else {
      textBuffer.push(line);
    }
  }

  flushText();
  flushCards();
  return blocks;
}

// ─── استخراج slug از URL ───
function extractSlug(url) {
  const parts = url.split('/').filter(Boolean);
  return parts[parts.length - 1] || '';
}

// ─── hook: دریافت اطلاعات کارت از endpoint ───
function useCardData(items, token) {
  const [cardData, setCardData] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  // ref برای دسترسی به cardData بدون نیاز به dependency
  const cardDataRef = React.useRef(cardData);
  React.useEffect(() => { cardDataRef.current = cardData; }, [cardData]);

  // key پایدار از items برای تشخیص تغییر واقعی
  const itemsKey = items ? items.map(i => i.url).join(',') : '';

  React.useEffect(() => {
    if (!items || items.length === 0 || !token) return;

    const missing = items.filter(
      item => !cardDataRef.current[`${item.itemType}:${item.url}`]
    );
    if (missing.length === 0) return;

    setLoading(true);
    const payload = missing.map(item => ({
      type: item.itemType === 'book' ? 'book' : 'product',
      slug: extractSlug(item.url),
      title: item.title,
    }));

    fetch('https://farzaneh.app/api/ai/cards/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ items: payload }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data || !data.results) return;
        const map = {};
        missing.forEach((item, idx) => {
          const result = data.results[idx];
          if (result) map[`${item.itemType}:${item.url}`] = result;
        });
        setCardData(prev => ({ ...prev, ...map }));
      })
      .catch(() => { /* fetch خطا — کارت با fallback نمایش داده می‌شود */ })
      .finally(() => setLoading(false));
  // eslint-disable-next-line
  }, [itemsKey, token]); // itemsKey جای items را می‌گیرد تا reference equality مشکل نسازد

  return { cardData, loading };
}

// ─── کارت کتاب کوچک ───
const SmallBookCard = ({ data }) => (
  <Link to={data.url} className="flex items-center gap-2 p-2 rounded-xl border border-gray-100 bg-white hover:bg-gray-50 transition group w-full">
    <div className="w-10 h-14 rounded-md overflow-hidden shrink-0 bg-gray-100">
      {data.cover_url
        ? <img src={data.cover_url} alt={data.title} className="w-full h-full object-cover" />
        : <div className="w-full h-full flex items-center justify-center text-gray-300 text-lg">📚</div>
      }
    </div>
    <span className="text-xs text-gray-700 leading-5 line-clamp-3 group-hover:text-indigo-600 transition text-right flex-1">
      {data.title}
    </span>
  </Link>
);

// ─── کارت محصول کوچک ───
const SmallProductCard = ({ data }) => {
  const isFree = data.is_free || (data.price !== null && data.price !== undefined && data.price <= 0);
  return (
    <Link to={data.url} className="flex items-center gap-2 p-2 rounded-xl border border-gray-100 bg-white hover:bg-gray-50 transition group w-full">
      <div className="w-10 h-10 rounded-md overflow-hidden shrink-0 bg-gray-100">
        {data.cover_url
          ? <img src={data.cover_url} alt={data.title} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center text-gray-300 text-lg">🎓</div>
        }
      </div>
      <div className="flex flex-col flex-1 min-w-0 text-right">
        <span className="text-xs text-gray-700 leading-5 line-clamp-2 group-hover:text-indigo-600 transition">
          {data.title}
        </span>
        {(isFree || data.price_display || data.price) && (
          <span className={`text-[10px] mt-0.5 font-medium ${isFree ? 'text-cyan-600' : 'text-indigo-600'}`}>
            {isFree ? 'رایگان' : (data.price_display || data.price)}
          </span>
        )}
      </div>
    </Link>
  );
};

// ─── بلوک کارت‌ها با fetch اطلاعات واقعی ───
const CardsBlock = ({ items, token }) => {
  const { cardData } = useCardData(items, token);

  return (
    <div className="flex flex-col gap-2 my-2 w-full max-w-[280px]">
      {items.map((item, idx) => {
        const key = `${item.itemType}:${item.url}`;
        const data = cardData[key] || {
          type: item.itemType,
          slug: extractSlug(item.url),
          title: item.title,
          cover_url: null,
          url: item.url,
        };

        if (item.itemType === 'book') {
          return <SmallBookCard key={idx} data={data} />;
        }
        return <SmallProductCard key={idx} data={data} />;
      })}
    </div>
  );
};

// ─── لینک قابل‌کلیک برای صفحات عادی ───
const PageLink = ({ title, url }) => (
  <Link
    to={url}
    className="inline-flex items-center gap-1 text-blue-600 underline underline-offset-2 hover:text-blue-800 transition-colors text-sm"
  >
    {title}
  </Link>
);

// ─── انیمیشن انتظار (loading) ───
const LoadingDots = () => (
  <div className="flex items-center gap-2 py-1">
    <span className="text-sm text-gray-600">در حال یافتن بهترین پاسخ برای شما هستم</span>
    <span className="flex gap-1">
      {[0, 1, 2].map(i => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// کامپوننت اصلی
// ─────────────────────────────────────────────────────────────────────────────
const AIPage = () => {
  const { isLoggedIn, openAuthModal, getAuthTokens } = useAuthContext();
  const navigate = useNavigate();

  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem('chatHistory');
      if (saved && JSON.parse(saved).length > 0) return JSON.parse(saved);
      return [{ id: 0, role: 'assistant', content: initialMessage }];
    } catch (e) {
      return [{ id: 0, role: 'assistant', content: initialMessage }];
    }
  });

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchWeb, setSearchWeb] = useState(false);
  const [deepSearch, setDeepSearch] = useState(true); // پیش‌فرض true — مطابق بک‌اند

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('chatHistory', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 140) + 'px';
  }, [input]);

  const getToken = () => {
    try {
      if (getAuthTokens && typeof getAuthTokens === 'function') {
        const tokens = getAuthTokens();
        if (tokens && tokens.access) return tokens.access;
        if (tokens && typeof tokens === 'string') return tokens;
      }
      return localStorage.getItem('access_token') || localStorage.getItem('token');
    } catch (e) {
      console.error('Error getting token:', e);
      return null;
    }
  };

  const sendMessage = async (userMessage, flags = {}) => {
    if (!isLoggedIn) { openAuthModal(); return; }
    const token = getToken();
    if (!token) { openAuthModal(); return; }
    if (!userMessage.trim()) return;

    const userMsgObj = { id: Date.now(), role: 'user', content: userMessage.trim() };
    setMessages(prev => [...prev, userMsgObj]);

    const assistantMsgId = Date.now() + 1;
    setMessages(prev => [...prev, { id: assistantMsgId, role: 'assistant', content: '' }]);
    setIsLoading(true);
    setInput('');

    if (abortControllerRef.current) abortControllerRef.current.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const response = await fetch('https://farzaneh.app/api/v1/ai/chat/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          query: userMessage,
          search_web: Boolean(flags.searchWeb),
          deep_search: Boolean(flags.deepSearch) // پیش‌فرض در بک‌اند: True
        }),
        signal: abortController.signal
      });

      if (!response.ok) {
        if (response.status === 401) {
          openAuthModal();
          throw new Error('نشست شما منقضی شده است. لطفاً دوباره وارد شوید.');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;
            try {
              const parsed = JSON.parse(data);
              if (parsed.type === 'meta') continue;
              if (parsed.type === 'sources') continue;
              if (parsed.text) {
                accumulatedText += parsed.text;
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMsgId ? { ...msg, content: accumulatedText } : msg
                ));
              }
            } catch (e) { /* ignore parse error */ }
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      setMessages(prev => prev.map(msg =>
        msg.id === assistantMsgId
          ? { ...msg, content: `⚠️ خطا: ${error.message || 'مشکل در ارتباط با سرور'}` }
          : msg
      ));
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (input.trim() && !isLoading) {
      sendMessage(input, { searchWeb, deepSearch });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleClose = () => navigate(-1);

  return ReactDOM.createPortal(
    <>
      <Helmet>
        <title>دستیار هوشمند | فرزانه</title>
      </Helmet>

      <div className="fixed z-[60] flex flex-col top-14 bottom-16 left-0 right-0
                      md:top-0 md:bottom-0 md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-2xl">
        {/* تصویر پس‌زمینه (موبایل) */}
        <div
          className="absolute inset-0 md:hidden"
          style={{
            backgroundImage: `url(${BG_IMAGE})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0,
          }}
        />
        <div className="absolute inset-0 bg-white/40 backdrop-blur-xl md:hidden" style={{ zIndex: 1 }} />
        <div className="absolute inset-0 hidden md:block bg-white" style={{ zIndex: 1 }} />

        {/* محتوای اصلی */}
        <div className="relative flex flex-col h-full w-full">
          {/* ─── هدر ─── */}
          <div className="flex items-center justify-between px-4 py-3 bg-white/60 backdrop-blur-md border-b border-white/30 shrink-0">
            <span className="font-bold text-gray-800 text-sm">دستیار هوشمند فرزانه</span>
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-white/50 transition"
              aria-label="بستن"
            >
              <BsX size={22} className="text-gray-700" />
            </button>
          </div>

          {/* ─── ناحیه پیام‌ها ─── */}
          <div className="flex-1 overflow-y-auto px-3 py-4">
            <div className="space-y-4">
              {messages.map((msg) => {
                const isUser = msg.role === 'user';

                const blocks = isUser
                  ? [{ type: 'text', value: msg.content }]
                  : parseMessageContent(msg.content);

                return (
                  <div
                    key={msg.id}
                    className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={[
                        'w-fit max-w-[88%] rounded-2xl px-4 py-3 shadow-sm',
                        isUser
                          ? 'bg-white/25 backdrop-blur-lg border border-white/50 text-gray-900 rounded-br-sm'
                          : 'bg-white text-gray-800 rounded-bl-sm border border-gray-100',
                      ].join(' ')}
                    >
                      {/* هدر حباب */}
                      <div className="flex justify-between items-center mb-1.5 text-xs opacity-60 gap-4">
                        <span className="font-semibold">{isUser ? 'شما' : 'فرزانه'}</span>
                        {!isUser && msg.content && (
                          <button
                            onClick={() => copyToClipboard(msg.content)}
                            className="hover:opacity-100"
                            aria-label="کپی"
                          >
                            <BsCopy size={12} />
                          </button>
                        )}
                      </div>

                      {/* ─── محتوای حباب ─── */}
                      <div className="text-sm leading-7 font-light text-right">
                        {blocks.map((block, i) => {
                          if (block.type === 'cards') {
                            return <CardsBlock key={i} items={block.items} token={getToken()} />;
                          }
                          if (block.type === 'link') {
                            return <PageLink key={i} title={block.title} url={block.url} />;
                          }
                          return (
                            <div
                              key={i}
                              className="whitespace-pre-wrap break-words"
                              style={{ overflowWrap: 'anywhere' }}
                            >
                              {block.value}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* پیام انتظار با انیمیشن */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white rounded-2xl px-4 py-3 border border-gray-100 shadow-sm">
                    <LoadingDots />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* نوار ورودی */}
          <div
            className="shrink-0 bg-white/80 backdrop-blur-md border-t border-white/40 px-3 pt-3"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
          >
            <div className="flex flex-col gap-2">
              {/* ردیف اصلی: textarea + دکمه ارسال */}
              <div className="flex items-end gap-2">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  }}
                  placeholder="سوال خود را اینجا بنویسید…"
                  rows={1}
                  className={[
                    'flex-1 min-h-[48px] max-h-[140px] px-4 py-3',
                    'bg-white border border-gray-200 rounded-2xl',
                    'outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100',
                    'transition-all text-sm resize-none leading-6',
                    'placeholder:text-gray-400 text-gray-800',
                    isLoading ? 'opacity-50 cursor-not-allowed' : '',
                  ].join(' ')}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!input.trim() || isLoading}
                  className={[
                    'h-[48px] w-[48px] rounded-2xl flex items-center justify-center shrink-0',
                    'transition-all duration-200',
                    input.trim() && !isLoading
                      ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-200 active:scale-95'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed',
                  ].join(' ')}
                  aria-label="ارسال پیام"
                >
                  <BsSend size={17} />
                </button>
              </div>

              {/* ردیف دوم: راهنما + دکمه‌های toggle */}
              <div className="flex items-center justify-between gap-2 px-0.5">
                <p className="text-[10px] text-gray-400 shrink-0">
                  Enter ارسال &nbsp;·&nbsp; Shift+Enter خط جدید
                </p>
                <div className="flex gap-2">

                  {/* جستجوی اینترنتی */}
                  <button
                    type="button"
                    onClick={() => setSearchWeb(p => !p)}
                    title={searchWeb ? 'جستجوی اینترنتی فعال است' : 'جستجو در اینترنت را فعال کنید'}
                    className={[
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 select-none',
                      searchWeb
                        ? 'bg-blue-600 text-white shadow-sm ring-2 ring-blue-200'
                        : 'bg-white border border-gray-200 text-gray-500 hover:border-blue-300 hover:text-blue-600',
                    ].join(' ')}
                  >
                    <BsSearch size={11} />
                    <span>جستجوی وب</span>
                    {searchWeb && <span className="w-1.5 h-1.5 rounded-full bg-white/90 inline-block" />}
                  </button>

                  {/* تفکر عمیق */}
                  <button
                    type="button"
                    onClick={() => setDeepSearch(p => !p)}
                    title={deepSearch ? 'تفکر عمیق فعال است (پیش‌فرض)' : 'تفکر عمیق را فعال کنید'}
                    className={[
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 select-none',
                      deepSearch
                        ? 'bg-indigo-600 text-white shadow-sm ring-2 ring-indigo-200'
                        : 'bg-white border border-gray-200 text-gray-500 hover:border-indigo-300 hover:text-indigo-600',
                    ].join(' ')}
                  >
                    <BsLightbulb size={11} />
                    <span>تفکر عمیق</span>
                    {deepSearch && <span className="w-1.5 h-1.5 rounded-full bg-white/90 inline-block" />}
                  </button>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default AIPage;